import { EntityKeySchema } from "@/providers/FundExplorer";

/**
 * Updates the current URL's search parameters with the specified key-value pairs.
 *
 * This function reads the current window URL and updates or adds search parameters based on
 * the provided `params` object. For each parameter:
 * - If the value is an array, empty strings are filtered out and the remaining elements are joined with commas.
 * - If the resulting string is empty, the parameter is removed from the URL.
 * - Otherwise, the parameter is set or updated with the resulting string.
 *
 * The URL is then updated using the History API without reloading the page.
 *
 * @param {Record<string, string | string[]>} params - An object where each key represents a search parameter name.
 *   The corresponding value is either a string or an array of strings. Array values are filtered for empty
 *   strings and then concatenated into a single comma-separated string.
 *
 * @returns {void}
 */
export default function applyURLSearchParams(
	params: Record<string, string | string[]>,
): void {
	const url = new URL(window.location.href);
	for (const [key, value] of Object.entries(params)) {
		const valueStr = Array.isArray(value)
			? value.filter((v) => v.length).join(",")
			: value;
		if (!valueStr || valueStr.length === 0) {
			url.searchParams.delete(key);
			continue;
		}
		url.searchParams.set(key, valueStr);
	}
	window.history.replaceState(null, "", url.toString());
}
